import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { MockApi } from "./api/mockApi";
import AiConsentModal from "./AiConsentModal";
import {
  AI_CONSENT_POLICY_VERSIONS,
  getConsentModalMessage,
  isConsentCheckCandidate,
  isConsentEnforcementHint,
  isForbiddenError,
  normalizeConsentPolicy,
  normalizeConsentStatus
} from "./consentConstants";
import { trackActivity } from "../utils/mixpanel";
import "./styles.css";

const AiConsentContext = createContext({
  accepted: false,
  checking: true,
  policy: null,
  /**
   * On 403: GET /api/ai/consent. Opens modal only if consent is still required.
   * @returns {Promise<boolean>} true when the consent modal was opened
   */
  requestReconsent: async () => false
});

export function useAiConsent() {
  return useContext(AiConsentContext);
}

/**
 * Gates all AI routes until consent is accepted.
 * Renders the destination page UI behind the modal while consent is pending.
 * On AI API 403, re-checks consent via GET before reopening the modal.
 */
export default function AiConsentGate({ children }) {
  const navigate = useNavigate();
  const [consentState, setConsentState] = useState("checking");
  const [consentMessage, setConsentMessage] = useState("");
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [reacceptanceRequired, setReacceptanceRequired] = useState(false);
  const [consentPolicy, setConsentPolicy] = useState(() =>
    normalizeConsentPolicy(AI_CONSENT_POLICY_VERSIONS)
  );

  const loadPolicy = useCallback(async () => {
    try {
      const policy = normalizeConsentPolicy(await MockApi.loadConsentPolicy());
      setConsentPolicy(policy);
      if (policy.requires_reacceptance) setReacceptanceRequired(true);
      return policy;
    } catch {
      return null;
    }
  }, []);

  const openConsentModal = useCallback(
    async ({
      reacceptance = false,
      saveFailed = false,
      // Only show the bottom notice after a mid-flow 403 consent failure (or save retry).
      showMessage = false
    } = {}) => {
      setReacceptanceRequired(reacceptance);
      setConsentMessage(showMessage || saveFailed ? getConsentModalMessage({ saveFailed }) : "");
      // Open immediately so a slow policy fetch cannot block the modal.
      setConsentState("required");
      await loadPolicy();
    },
    [loadPolicy]
  );

  /**
   * Mid-flow enforcement handling:
   * 1) Confirm with GET /api/ai/consent
   * 2) Open modal only when consent is required (or consent GET itself is 403)
   * A CORS-blocked 403 reaches us as "Network Error", so those are verified too.
   * Returns true only when the modal was opened.
   */
  const requestReconsent = useCallback(
    async (error) => {
      if (error && !isConsentCheckCandidate(error)) return false;

      try {
        const consent = normalizeConsentStatus(await MockApi.loadConsentStatus());
        if (!consent.required) {
          // GET says access is fine — do not treat this failure as consent.
          // Exception: payload explicitly signals terms/consent enforcement.
          if (!isConsentEnforcementHint(error)) {
            return false;
          }
        }
        await openConsentModal({
          reacceptance: Boolean(consent.reacceptanceRequired || consent.required),
          showMessage: true
        });
        return true;
      } catch (statusError) {
        // Consent status endpoint itself forbidden under enforcement.
        if (isForbiddenError(statusError) || isConsentEnforcementHint(error)) {
          await openConsentModal({ reacceptance: true, showMessage: true });
          return true;
        }
        return false;
      }
    },
    [openConsentModal]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadConsent() {
      try {
        const consent = normalizeConsentStatus(await MockApi.loadConsentStatus());
        if (cancelled) return;

        setReacceptanceRequired(consent.reacceptanceRequired);

        if (!consent.required) {
          setConsentMessage("");
          setConsentState("accepted");
          return;
        }

        // Fresh entry: show modal without the mid-flow consent-failure notice.
        await openConsentModal({
          reacceptance: Boolean(consent.reacceptanceRequired),
          showMessage: false
        });
      } catch {
        if (cancelled) return;
        // Cannot verify consent — still require acceptance before AI use (no mid-flow notice).
        await openConsentModal({ showMessage: false });
      }
    }

    loadConsent();
    return () => {
      cancelled = true;
    };
  }, [openConsentModal]);

  async function handleConsentAccept() {
    if (consentSubmitting) return;

    setConsentSubmitting(true);
    try {
      let policy = consentPolicy;
      try {
        policy = normalizeConsentPolicy(await MockApi.loadConsentPolicy());
        setConsentPolicy(policy);
      } catch {
        // Use previously loaded policy if refresh fails.
      }

      const consent = normalizeConsentStatus(
        await MockApi.acceptConsent({
          terms_version: policy.terms_version,
          data_policy_version: policy.data_policy_version,
          privacy_policy_version: policy.privacy_policy_version
        })
      );
      if (consent.required) {
        throw new Error("Consent remains required.");
      }

      trackActivity(
        reacceptanceRequired ? "ai_reconsent_accepted" : "ai_consent_accepted",
        { page_title: "AI Chat Assistant" }
      );
      setReacceptanceRequired(false);
      setConsentMessage("");
      setConsentState("accepted");
    } catch (error) {
      setConsentMessage(getConsentModalMessage({ saveFailed: true }));
      trackActivity("ai_consent_failed", { page_title: "AI Chat Assistant" });
    } finally {
      setConsentSubmitting(false);
    }
  }

  function handleConsentCancel() {
    trackActivity("ai_consent_cancelled", { page_title: "AI Chat Assistant" });
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/leases", { replace: true });
  }

  const contextValue = useMemo(
    () => ({
      accepted: consentState === "accepted",
      checking: consentState === "checking",
      policy: consentPolicy,
      requestReconsent
    }),
    [consentState, consentPolicy, requestReconsent]
  );

  if (consentState === "checking") {
    return (
      <div className="ai-chat-app">
        <main className="loading-screen">
          <CircularProgress size={28} />
          <span>Checking AI consent</span>
        </main>
      </div>
    );
  }

  return (
    <AiConsentContext.Provider value={contextValue}>
      <div
        className={consentState === "required" ? "ai-consent-page-blocked" : undefined}
        aria-hidden={consentState === "required" ? "true" : undefined}
      >
        {children}
      </div>
      <AiConsentModal
        open={consentState === "required"}
        submitting={consentSubmitting}
        message={consentMessage}
        reacceptance={reacceptanceRequired}
        policy={consentPolicy}
        onAccept={handleConsentAccept}
        onCancel={handleConsentCancel}
      />
    </AiConsentContext.Provider>
  );
}
